import { useEffect, useMemo, useState } from "react";
import { FileText, Download, Pencil, Trash2, Search, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import InvoiceEditor from "@/components/InvoiceEditor";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { formatEuro2, formatDateEU } from "@/lib/constants";

const STATUS_STYLES = {
  Draft:            { bg: "#6C757D26", color: "#6C757D" },
  Sent:             { bg: "#1F4D3A26", color: "#1F4D3A" },
  "Partially Paid": { bg: "#E85D0426", color: "#E85D04" },
  Paid:             { bg: "#2A9D8F26", color: "#2A9D8F" },
  Cancelled:        { bg: "#D6282826", color: "#D62828" },
};

const STATUS_OPTIONS = ["Draft", "Sent", "Partially Paid", "Paid", "Cancelled"];

export default function Invoices() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("all"); // "all" | "YYYY-MM"
  const [editInvoice, setEditInvoice] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listInvoices();
      setInvoices(data || []);
    } catch (e) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Unique months present in invoices, sorted DESC
  const monthOptions = useMemo(() => {
    const set = new Set();
    for (const inv of invoices) {
      const d = inv.invoice_date || "";
      if (d && d.length >= 7) set.add(d.slice(0, 7));
    }
    return [...set].sort().reverse();
  }, [invoices]);

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      if (monthFilter !== "all") {
        const d = inv.invoice_date || "";
        if (d.slice(0, 7) !== monthFilter) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = `${inv.invoice_number} ${inv.customer_name}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [invoices, statusFilter, monthFilter, search]);

  const stats = useMemo(() => {
    const total = filtered.reduce((s, i) => s + Number(i.total || 0), 0);
    const paid = filtered.filter((i) => i.status === "Paid").reduce((s, i) => s + Number(i.total || 0), 0);
    const unpaid = filtered.filter((i) => i.status !== "Paid" && i.status !== "Cancelled").reduce((s, i) => s + Number(i.total || 0), 0);
    return { count: filtered.length, total, paid, unpaid };
  }, [filtered]);

  const doExport = async () => {
    try {
      const filters = {};
      if (statusFilter !== "all") filters.status = statusFilter;
      if (monthFilter !== "all") {
        const [y, m] = monthFilter.split("-");
        filters.year = y;
        filters.month = m;
      }
      await api.exportInvoicesCsv(filters);
      toast.success("Invoices exported successfully");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const changeStatus = async (inv, newStatus) => {
    if (newStatus === inv.status) return;
    try {
      await api.changeInvoiceStatus(inv.id, newStatus);
      toast.success(`${inv.invoice_number} → ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change status");
    }
  };

  const del = async (inv) => {
    if (!isAdmin) return;
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? This action cannot be undone.`)) return;
    try {
      await api.deleteInvoice(inv.id);
      toast.success(`${inv.invoice_number} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div data-testid="page-invoices">
      <PageHeader
        overline="Finance"
        title="Invoices"
        subtitle="Sequential monthly numbering (MMYYYY-N) · CSV export for accounting."
        actions={
          <Button
            onClick={doExport}
            className="bg-[#E85D04] hover:bg-[#c94b03] text-white"
            data-testid="export-invoices-btn"
            disabled={filtered.length === 0}
            title={filtered.length === 0 ? "No invoices match the current filters" : "Download filtered results as CSV"}
          >
            <Download size={16} className="mr-2" />
            Export Invoices ({filtered.length})
          </Button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Kpi label="Total"   value={formatEuro2(stats.total)}  testId="kpi-total" />
        <Kpi label="Paid"    value={formatEuro2(stats.paid)}   testId="kpi-paid"   accent="#2A9D8F" />
        <Kpi label="Unpaid"  value={formatEuro2(stats.unpaid)} testId="kpi-unpaid" accent="#E85D04" />
        <Kpi label="Invoices" value={stats.count} testId="kpi-count" accent="#1F4D3A" />
      </div>

      {/* Filters */}
      <div className="card-soft p-4 mb-5 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search number or customer…"
            className="pl-9 bg-white border-[#E5E5E5]"
            data-testid="invoice-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-white border-[#E5E5E5]" data-testid="invoice-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger className="bg-white border-[#E5E5E5]" data-testid="invoice-month-filter">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All months</SelectItem>
            {monthOptions.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 text-xs text-[#6C757D] px-2">
          <Filter size={12} />
          Showing {filtered.length} of {invoices.length}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#6C757D]">Loading invoices…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No invoices"
          description={invoices.length === 0
            ? "Invoices generated from Orders will appear here."
            : "Adjust filters to see more invoices."}
        />
      ) : (
        <div className="card-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead className="bg-[#F7F1E8] text-[#6C757D] uppercase text-[10px] tracking-wider">
                <tr>
                  <Th>Number</Th>
                  <Th>Date</Th>
                  <Th>Customer</Th>
                  <Th className="text-right">Subtotal</Th>
                  <Th className="text-right">VAT %</Th>
                  <Th className="text-right">Total</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => {
                  const s = STATUS_STYLES[inv.status] || STATUS_STYLES.Draft;
                  return (
                    <tr key={inv.id} className="border-t border-[#E5E5E5] hover:bg-[#F7F1E8]/40" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-3 py-2 font-mono font-semibold text-[#1F4D3A]" data-testid={`invoice-number-${inv.id}`}>
                        {inv.invoice_number}
                      </td>
                      <td className="px-3 py-2 text-[#6C757D]">{formatDateEU(inv.invoice_date)}</td>
                      <td className="px-3 py-2 font-medium text-[#222] truncate max-w-[180px]" title={inv.customer_name}>
                        {inv.customer_name}
                      </td>
                      <td className="px-3 py-2 text-right text-[#6C757D]">{formatEuro2(inv.subtotal)}</td>
                      <td className="px-3 py-2 text-right text-[#6C757D]">{Number(inv.vat_pct || 0).toFixed(2)}%</td>
                      <td className="px-3 py-2 text-right font-bold text-[#E85D04]">{formatEuro2(inv.total)}</td>
                      <td className="px-3 py-2">
                        <select
                          value={inv.status}
                          onChange={(e) => changeStatus(inv, e.target.value)}
                          className="text-[10px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wider border-0 cursor-pointer"
                          style={{ backgroundColor: s.bg, color: s.color }}
                          data-testid={`invoice-status-${inv.id}`}
                          title="Change status"
                        >
                          {STATUS_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => setEditInvoice(inv)}
                          className="p-1.5 rounded hover:bg-[#F7F1E8] text-[#6C757D] hover:text-[#E85D04]"
                          data-testid={`invoice-edit-${inv.id}`}
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => api.downloadInvoicePdf(inv.id, inv.invoice_number)}
                          className="p-1.5 rounded hover:bg-[#F7F1E8] text-[#6C757D] hover:text-[#1F4D3A]"
                          data-testid={`invoice-pdf-${inv.id}`}
                          title="Download PDF"
                        >
                          <Download size={14} />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => del(inv)}
                            className="p-1.5 rounded hover:bg-[#D62828]/10 text-[#6C757D] hover:text-[#D62828]"
                            data-testid={`invoice-delete-${inv.id}`}
                            title="Delete (admin)"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <InvoiceEditor
        open={!!editInvoice}
        existingInvoice={editInvoice}
        onClose={() => { setEditInvoice(null); load(); }}
        onSaved={() => { load(); }}
      />
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2 text-left font-semibold ${className}`}>{children}</th>
);

const Kpi = ({ label, value, testId, accent = "#222" }) => (
  <div className="card-soft p-4" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#6C757D] font-bold">{label}</div>
    <div className="font-display font-bold text-2xl mt-1" style={{ color: accent }}>{value}</div>
  </div>
);
